import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Tooltip } from '../components/ui/Tooltip';
import { ActivityDetailModal } from '../components/operations/ActivityDetailModal';
import { EditActivityModal } from '../components/operations/EditActivityModal';
import { GenerateActivityWizardModal } from '../components/operations/GenerateActivityWizardModal';
import { operativeActivitiesStore, getProgress, getCurrentStepName, OperativeActivity } from '../services/operativeActivities';
import { Plus, Filter, Download, Eye, Edit, Search, X, Trash2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
interface TableRow {
  id: string;
  type: 'standard' | 'personalizzata';
  code: string;
  client: string;
  contract: string;
  product: string;
  operator: string;
  stepName: string;
  percentage: number;
  isCompleted: boolean;
  deadline: string;
  rawActivity: OperativeActivity;
}
function formatDateIT(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return isoDate;
  }
}
function buildTableRows(): TableRow[] {
  const activities = operativeActivitiesStore.getAll();
  return activities.map((act, idx) => {
    const progress = getProgress(act);
    const stepName = getCurrentStepName(act);
    return {
      id: act.id,
      type: act.activityType,
      code: `ATT-2025/${String(idx + 1).padStart(3, '0')}`,
      client: act.clientName,
      contract: act.contractNumber,
      product: act.product,
      operator: act.operator,
      stepName,
      percentage: progress.percentage,
      isCompleted: progress.isCompleted,
      deadline: formatDateIT(act.deadline),
      rawActivity: act
    };
  });
}
const columnsConfig = [{
  key: 'tipo',
  label: 'Tipo'
}, {
  key: 'code',
  label: 'Codice'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'contract',
  label: 'Contratto'
}, {
  key: 'product',
  label: 'Prodotto'
}, {
  key: 'operator',
  label: 'Operatore'
}, {
  key: 'avanzamento',
  label: 'Avanzamento'
}, {
  key: 'deadline',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function OperationsPage() {
  const location = useLocation();
  const [selectedActivity, setSelectedActivity] = useState<OperativeActivity | null>(null);
  const [isActivityDetailOpen, setIsActivityDetailOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGenerateActivityOpen, setIsGenerateActivityOpen] = useState(false);
  const [editActivityForWizard, setEditActivityForWizard] = useState<OperativeActivity | null>(null);
  const [wizardContractNumber, setWizardContractNumber] = useState<string | undefined>();
  const [wizardClientName, setWizardClientName] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [viewMode, setViewMode] = useState<'aperte' | 'completate'>('aperte');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TableRow | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [, setRefreshKey] = useState(0);
  const {
    addNotification
  } = useNotifications();
  const allRows = useMemo(() => buildTableRows(), []);
  const baseData = useMemo(() => viewMode === 'aperte' ? allRows.filter(r => !r.isCompleted) : allRows.filter(r => r.isCompleted), [viewMode, allRows]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(baseData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['code', 'client', 'contract', 'product', 'operator']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, viewMode]);
  // Handle navigation from ContractsPage to open wizard
  useEffect(() => {
    const state = location.state as any;
    if (state?.openWizard) {
      setWizardContractNumber(state.contractNumber);
      setWizardClientName(state.clientName);
      setIsGenerateActivityOpen(true);
      // Clear the state so it doesn't re-open on re-render
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  const handleView = (row: TableRow) => {
    setSelectedActivity(row.rawActivity);
    setIsActivityDetailOpen(true);
  };
  const handleEdit = (row: TableRow) => {
    setEditActivityForWizard(row.rawActivity);
    setIsGenerateActivityOpen(true);
  };
  const handleDelete = (row: TableRow) => {
    setItemToDelete(row);
    setShowDeleteDialog(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
      addNotification('success', 'Eliminato', 'Attività eliminata con successo');
    }, 1500);
  };
  const handleActivityStepComplete = (activityId: string, stepIndex: number) => {
    operativeActivitiesStore.completeStep(activityId, stepIndex);
    const updated = operativeActivitiesStore.getById(activityId);
    if (updated) setSelectedActivity({
      ...updated
    });
    setRefreshKey(r => r + 1);
  };
  const handleEditFromDetail = (activity: OperativeActivity) => {
    setIsActivityDetailOpen(false);
    setTimeout(() => {
      setEditActivityForWizard(activity);
      setIsGenerateActivityOpen(true);
    }, 200);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-5579" data-mp="24u30ltmoxqiq">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-5580" data-mp="24ul337zy50z3">
        <div data-id="element-5581" data-mp="3b8khcqdg9yez">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-5582" data-mp="3b93vrx3w64o9">
            Attività operative
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-5583" data-mp="3g6vpmwo3k0v3">
            Pianificazione e monitoraggio delle attività operative.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5584" data-mp="zmilggbd9ekq">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-5585" data-mp="zms8bv1475m7">
            <button onClick={() => setViewMode('aperte')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'aperte' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5586" data-mp="uuie42syvje4">
              Aperte
            </button>
            <button onClick={() => setViewMode('completate')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${viewMode === 'completate' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5587" data-mp="gu7muynqw2sf">
              Completate
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-5589" data-mp="1a69wivhh73vj" />} data-id="element-5588" data-mp="21bvr8jfxe7wp">
            Esporta
          </Button>
          <Button startContent={<Plus className="h-4 w-4" data-id="element-5591" data-mp="1f5mi7cwm24wj" />} onClick={() => {
          setWizardContractNumber(undefined);
          setWizardClientName(undefined);
          setIsGenerateActivityOpen(true);
        }} data-id="element-5590" data-mp="1a8mev9r5bg94">
            Nuova attività
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-5592" data-mp="reqvgu70hzw5">
        <div className="p-4 flex flex-col gap-4" data-id="element-5593" data-mp="rhv6obiw9vwb">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-5594" data-mp="wf53wdcizbup">
            <div className="flex-1" data-id="element-5595" data-mp="wciojrje7s1x">
              <Input placeholder="Cerca codice, cliente, contratto, prodotto, operatore..." startContent={<Search className="h-4 w-4" data-id="element-5597" data-mp="mh7hzwi51r59" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-5596" data-mp="hie5a6aoe465" />
            </div>
            <div className="flex gap-2" data-id="element-5598" data-mp="2gu6sjb7nvv5o">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-5600" data-mp="6fmrou3d3ox" />} data-id="element-5599" data-mp="57d3dwzgdpob">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-5601" data-mp="a66exjv6j6lu">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="operations" lockedColumns={['code']} data-id="element-5602" data-mp="jv7vbb5mzq0c" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-5603" data-mp="2znnn2f6fbc8d">
              <Select label="Tipo" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'standard',
            label: 'Standard'
          }, {
            value: 'personalizzata',
            label: 'Personalizzata'
          }]} data-id="element-5604" data-mp="2qh56bf9hore3" />
              <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }, {
            value: 'Tiziano',
            label: 'Tiziano'
          }]} data-id="element-5605" data-mp="2zh1y3pr6e6dd" />
              <div className="flex items-end" data-id="element-5606" data-mp="1snw1j9dt22p6">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-5608" data-mp="22e73wrhuc3ty" />} data-id="element-5607" data-mp="1sl03ll1yje0q">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden relative" data-id="element-5609" data-mp="2bewxrpnjjk51">
        <div className="overflow-x-auto" data-id="element-5610" data-mp="2gcf8nll4c9fo">
          <table className="w-full text-left border-collapse table-auto" data-id="element-5611" data-mp="2gdgm1wplx80k">
            <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 shadow-sm" data-id="element-5612" data-mp="g9nu8e1damdq">
              <tr className="text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-5613" data-mp="bc44ohz7yn6h">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-3 py-3 whitespace-nowrap ${col.key === 'actions' ? 'w-28' : ''} ${col.key === 'avanzamento' ? 'min-w-[220px]' : ''}`} data-id="element-5614" data-mp="l7xpspr07ozv">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5615" data-mp="1409pt5u6qe6u">
              {paginatedData.map(row => <tr key={row.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-5616" data-mp="3aq7xo22shjv1">
                  {visibleColumns.includes('tipo') && <td className="px-3 py-3 whitespace-nowrap" data-id="element-5617" data-mp="1l8ssgi99u1ll">
                      <Badge variant={row.type === 'standard' ? 'info' : 'warning'} size="sm" data-id="element-5618" data-mp="1lbopm4daom8z">
                        {row.type === 'standard' ? 'Standard' : 'Personalizzata'}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('code') && <td className="px-3 py-3 font-mono text-xs text-slate-500 whitespace-nowrap" data-id="element-5619" data-mp="17igyedt4lsrt">
                      {row.code}
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-3 py-3 text-sm text-slate-700 truncate max-w-[160px]" title={row.client} data-id="element-5620" data-mp="xkjdo0ah6e3y">
                      {row.client}
                    </td>}
                  {visibleColumns.includes('contract') && <td className="px-3 py-3 font-mono text-xs text-slate-500 whitespace-nowrap" data-id="element-5621" data-mp="ruqd1mbmu5u3">
                      {row.contract}
                    </td>}
                  {visibleColumns.includes('product') && <td className="px-3 py-3 text-sm text-slate-600 truncate max-w-[140px]" title={row.product} data-id="element-5622" data-mp="dw0es3d2u045">
                      {row.product}
                    </td>}
                  {visibleColumns.includes('operator') && <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5623" data-mp="3k7gqd57fxf">
                      {row.operator}
                    </td>}
                  {visibleColumns.includes('avanzamento') && <td className="px-3 py-3" data-id="element-5624" data-mp="3mas8xhkxz2o6">
                      <div className="flex items-center justify-between gap-3" data-id="element-5625" data-mp="3hbywlshe5hpl">
                        <span className={`text-sm truncate ${row.isCompleted ? 'text-green-700 font-semibold' : 'text-slate-700'}`} title={row.stepName} data-id="element-5626" data-mp="378k3w1y7cdjv">
                          {row.stepName}
                        </span>
                        <span className={`flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded-md border ${row.percentage === 100 ? 'bg-green-50 text-green-700 border-green-200' : row.percentage === 0 ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-orange-50 text-seform-orange border-orange-200'}`} data-id="element-5627" data-mp="3m5t2ngpu4jlm">
                          {row.percentage}%
                        </span>
                      </div>
                    </td>}
                  {visibleColumns.includes('deadline') && <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5628" data-mp="25raz2o1zzdgf">
                      {row.deadline}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-3 py-3 w-28" data-id="element-5629" data-mp="1xqv5a36myuqr">
                      <div className="flex items-center gap-1" data-id="element-5630" data-mp="1xq3inhv0o7cm">
                        <Tooltip content="Visualizza attività" data-id="element-5631" data-mp="2cgasxaavek28">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(row)} data-id="element-5632" data-mp="2chlynfnvm1vz">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-5633" data-mp="1et2ge4suklcd" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica attività" data-id="element-5634" data-mp="1tn6xpki07z5i">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(row)} data-id="element-5635" data-mp="1op5emzkkvzu1">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-5636" data-mp="307okvf540dmo" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina attività" data-id="element-5637" data-mp="3cozfnzyiknqd">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(row)} data-id="element-5638" data-mp="2yo6jlaskdkl5">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-5639" data-mp="3qgnpfwlaj046" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
              {paginatedData.length === 0 && <tr data-id="element-5640" data-mp="3lnbfmsl9sxvh">
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center text-slate-400 text-sm" data-id="element-5641" data-mp="3lmtbl9sd9fd0">
                    Nessuna attività trovata.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-5642" data-mp="3knkn1u68rq3i">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-5643" data-mp="3kmsrukgk3udr" />
        </div>
      </Card>

      {/* Activity Detail Modal */}
      <ActivityDetailModal isOpen={isActivityDetailOpen} onClose={() => setIsActivityDetailOpen(false)} activity={selectedActivity} onStepComplete={handleActivityStepComplete} onEdit={handleEditFromDetail} data-id="element-5644" data-mp="c6zl2xc11x9w" />

      {/* Edit Activity Modal */}
      <EditActivityModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} activity={selectedActivity} onSave={() => {
      setRefreshKey(r => r + 1);
    }} data-id="element-5645" data-mp="3tfcsfqges918" />

      {/* Generate Activity Wizard Modal */}
      <GenerateActivityWizardModal isOpen={isGenerateActivityOpen} onClose={() => {
      setIsGenerateActivityOpen(false);
      setTimeout(() => setEditActivityForWizard(null), 300);
    }} contractNumber={wizardContractNumber} clientName={wizardClientName} editActivity={editActivityForWizard || undefined} data-id="element-5646" data-mp="xjgcprmq5f3z" />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} title="Conferma eliminazione" size="sm" data-id="element-5647" data-mp="1h5mg1kyupy0c">
        <AnimatePresence mode="wait" data-id="element-5648" data-mp="1q7cehar5hx7m">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-5649" data-mp="1q85ii3yq68m2">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-5650" data-mp="24umult2epy4m">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-5651" data-mp="2skh5i5361cm8" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-5652" data-mp="2xmg83cr305i4">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-5653" data-mp="137m1poor0oyu">
              <div className="space-y-6" data-id="element-5654" data-mp="zppx027lmnf4">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-5655" data-mp="zqhkepr1leub">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-5656" data-mp="uwwyhw2q4bbh" />
                  <div data-id="element-5657" data-mp="q0erepjx80vz">
                    <p className="text-sm text-red-700 font-medium" data-id="element-5658" data-mp="pzmw7fu8k568">
                      Attenzione: questa azione è irreversibile.
                    </p>
                    <p className="text-sm text-red-600 mt-1" data-id="element-5659" data-mp="1ioao3u9hpssr">
                      L'attività <strong data-id="element-5660" data-mp="1doxzp52z0do2">{itemToDelete?.code}</strong> verrà
                      eliminata definitivamente.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-5661" data-mp="19p74mp6evbdq">
                  <Button variant="flat" color="default" onClick={() => setShowDeleteDialog(false)} data-id="element-5662" data-mp="14nhst7bd6tol">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} startContent={<Trash2 className="h-4 w-4" data-id="element-5664" data-mp="3vuif4hpx22j3" />} data-id="element-5663" data-mp="3pgv00g5s0vuf">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}